<script setup lang="ts">
import type { UIColor } from "@align/ui";
import { hasValue } from "~/helpers/common-helpers";

defineProps({
  colorBackground: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorSeperators: {
    type: String as PropType<UIColor>,
    required: true,
  },

  shadow: {
    type: Boolean,
    default: false,
  },

  rounded: {
    type: Boolean,
    default: false,
  },

  alignControlsEnd: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <Composition>
    <template #layouts>
      <WidgetLayout :colorBackground="colorSeperators" :rounded="rounded" :shadow="shadow">
        <template #widgetRows>
          <template v-if="!$slots.controlsStart && !$slots.controlsEnd && !$slots.info">
            <WidgetLayoutRowCellSingle>
              <template #widget>
                <WidgetBlank :colorBackground="colorBackground" omitClickMore>
                  <template #container>
                    <slot name="header" />

                    <CompositionSpacer direction="vertical" size="blip" />

                    <slot name="byline" />
                  </template>
                </WidgetBlank>
              </template>
            </WidgetLayoutRowCellSingle>

            <WidgetLayoutRowCellSingle v-if="$slots.keyFigures">
              <template #widget>
                <WidgetBlank :colorBackground="colorBackground" omitPadding omitClickMore>
                  <template #container>
                    <slot name="keyFigures" />
                  </template>
                </WidgetBlank>
              </template>
            </WidgetLayoutRowCellSingle>
          </template>

          <template v-else>
            <WidgetLayoutRowCellDouble offsetSplit reverseSplit>
              <template #widgetStart>
                <WidgetBlank :colorBackground="colorBackground" omitPaddingBottom omitClickMore>
                  <template #container>
                    <slot name="header" />

                    <CompositionSpacer v-if="$slots.byline" direction="vertical" size="blip" />

                    <slot name="byline" />
                  </template>
                </WidgetBlank>

                <WidgetBlank v-if="$slots.keyFigures" :colorBackground="colorBackground" omitPadding omitClickMore>
                  <template #container>
                    <slot name="keyFigures" />
                  </template>
                </WidgetBlank>
              </template>

              <template #widgetEnd>
                <div v-if="$slots.controlsStart" :class="$style.row">
                  <WidgetBlank :colorBackground="colorBackground" omitClickMore>
                    <template #container>
                      <slot name="controlsStart" />
                    </template>
                  </WidgetBlank>
                </div>

                <div v-if="$slots.controlsEnd" :class="$style.row">
                  <WidgetBlank :colorBackground="colorBackground" omitClickMore :alignItemsEnd="alignControlsEnd">
                    <template #container>
                      <slot name="controlsEnd" />
                    </template>
                  </WidgetBlank>
                </div>

                <div v-if="$slots.info" :class="$style.row">
                  <WidgetBlank :colorBackground="colorBackground" omitClickMore>
                    <template #container>
                      <slot name="info" />
                    </template>
                  </WidgetBlank>
                </div>
              </template>
            </WidgetLayoutRowCellDouble>
          </template>

          <WidgetLayoutRowCellSingle v-if="$slots.chips">
            <template #widget>
              <WidgetBlank :colorBackground="colorBackground" omitPadding omitClickMore>
                <template #container>
                  <slot name="chips" />
                </template>
              </WidgetBlank>
            </template>
          </WidgetLayoutRowCellSingle>

          <WidgetLayoutRowCellSingle v-if="$slots.controlsBottom">
            <template #widget>
              <WidgetBlank :colorBackground="colorBackground" omitClickMore>
                <template #container>
                  <slot name="controlsBottom" />
                </template>
              </WidgetBlank>
            </template>
          </WidgetLayoutRowCellSingle>
        </template>
      </WidgetLayout>
    </template>
  </Composition>
</template>

<style module>
.row {
  display: flex;
  width: 100%;
  height: 100%;
}

.row:not(:last-child) {
  margin-bottom: 1px;
}

.cell {
  display: flex;
  width: 100%;
  height: 100%;
}

.cell:not(:last-child) {
  margin-right: 1px;
}
</style>
